import { useEffect, useRef, useState } from "react";
import useTranslation from "next-translate/useTranslation";
import { useUser } from "@/redux/user";
import { trackEvent } from "@/services/eventTracker";
import RegisterPopup from "../register/RegisterPopup";
import useLearningOverview from "./useLearningOverview";
import type { LearningOverview } from "./learningTypes";

const DISMISSED = "chesshotel:save-progress-dismissed:v1";
export function guestInvitationEligible(data?: LearningOverview | null) {
  if (
    !data?.guest ||
    !data.persistent ||
    !((data.lessonsCompleted || 0) > 0 || data.chessStreak.streak >= 3)
  )
    return false;
  try {
    const until = Number(window.localStorage.getItem(DISMISSED));
    return !until || until < Date.now();
  } catch {
    return false;
  }
}
export default function GuestProgressInvitation({
  enabled = true,
  entrySource = "learning",
}: {
  enabled?: boolean;
  entrySource?: string;
}) {
  const { memberLoggedIn } = useUser();
  const { data } = useLearningOverview();
  const { t } = useTranslation("common");
  const [register, setRegister] = useState(false),
    [dismissed, setDismissed] = useState(false);
  const visible =
    enabled && !memberLoggedIn && !dismissed && guestInvitationEligible(data);
  const seen = useRef(false),
    element = useRef<HTMLElement>(null);
  useEffect(() => {
    if (
      !visible ||
      seen.current ||
      !element.current ||
      typeof IntersectionObserver === "undefined"
    )
      return;
    const observer = new IntersectionObserver(([e]) => {
      if (e.isIntersecting) {
        seen.current = true;
        observer.disconnect();
        void trackEvent("guest_progress_offered", {
          entrySource,
          flowVersion: 1,
        });
      }
    });
    observer.observe(element.current);
    return () => observer.disconnect();
  }, [visible, entrySource]);
  const dismiss = () => {
    setDismissed(true);
    try {
      window.localStorage.setItem(DISMISSED, String(Date.now() + 7 * 86400000));
    } catch {}
    void trackEvent("guest_progress_dismissed", {
      entrySource,
      flowVersion: 1,
    });
  };
  return (
    <>
      {visible && (
        <section
          ref={element}
          className="flex flex-wrap items-center gap-x-4 gap-y-1 border-t border-athens-gray-200 py-3 text-sm"
        >
          <p className="w-full min-w-0 text-slate-600 sm:w-auto sm:flex-1">
            {t("guestProgress.invitation")}
          </p>
          <button
            type="button"
            className="min-h-[44px] text-altblue-600 hover:underline"
            onClick={() => {
              setRegister(true);
              void trackEvent("guest_progress_save_selected", {
                entrySource,
                flowVersion: 1,
              });
            }}
          >
            {t("guestProgress.save")}
          </button>
          <button
            type="button"
            className="min-h-[44px] text-slate-500 hover:underline"
            onClick={dismiss}
          >
            {t("guestProgress.later")}
          </button>
        </section>
      )}
      {register && (
        <RegisterPopup
          title={t("guestProgress.save")}
          subtitle={t("guestProgress.accountBenefit")}
          onClose={() => setRegister(false)}
          guestLink={
            <button
              type="button"
              className="min-h-[44px] text-slate-500 hover:underline"
              onClick={() => {
                setRegister(false);
                dismiss();
              }}
            >
              {t("guestProgress.continueGuest")}
            </button>
          }
        />
      )}
    </>
  );
}
